import React, { useState, cloneElement, useContext, useMemo } from "react";

import {
  Tabs,
  Tab,
  TabHeaders,
  TabHeader,
  TabBadge,
  Loader,
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";

import { LibraryTable } from "./library.table";
import { SessionContext } from "@src/context/session.context";
import TabErrorBoundary from "@src/components/error-boundary/tab-error-boundary";
import { Button } from "@transfr-inc/dashboard-components/forms";

import "./library.tabs.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OrgsModal } from "@src/components/modals/orgs/orgs.modal";

const TAB_NAMES = {
  ACTIVITIES: "library-activities",
};

const DATA_TYPES = {
  ACTIVITIES: "Activities",
};

const LibraryTab = ({
  name,
  data,
  dataType,
  responsiveStyles,
  actionMenuOptions,
  isMenuActionVisible,
  openActivityOrgsModal,
  loading = false,
  error,
  onRetry,
  children,
}) => {
  if (loading) {
    return (
      <Tab name={name}>
        <div className="tab-loading-container">
          <Loader />
          <p>Loading {dataType}...</p>
        </div>
      </Tab>
    );
  }

  if (error) {
    return (
      <Tab name={name}>
        <div className="tab-error-boundary">
          <Notification
            type={NotificationType.error}
            icon={["fa-solid", "exclamation-triangle"]}
            closable={false}
          >
            <div className="error-content">
              <h4>Failed to load {dataType}</h4>
              <p>
                There was an error loading this section. This might be due to a
                network issue or server problem.
              </p>
              <div className="error-actions">
                <Button onClick={onRetry} primary>
                  Try Again
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
              {process.env.NODE_ENV === "development" && error && (
                <details className="error-details">
                  <summary>Error Details (Development)</summary>
                  <pre>{error.toString()}</pre>
                </details>
              )}
            </div>
          </Notification>
        </div>
      </Tab>
    );
  }

  return (
    <Tab name={name}>
      <TabErrorBoundary
        tabName={dataType}
        onRetry={onRetry}
        onRefresh={() => window.location.reload()}
      >
        <LibraryTable
          className="library-table"
          data={data || []}
          dataType={dataType}
          responsiveStyles={responsiveStyles}
          actionMenuOptions={actionMenuOptions}
          isMenuActionVisible={isMenuActionVisible}
          openActivityOrgsModal={openActivityOrgsModal}
        />
      </TabErrorBoundary>
      {children}
    </Tab>
  );
};

export const LibraryTabs = ({
  data,
  responsiveStyles,
  actionMenuOptions,
  tabStates = {},
  onRetryTab,
  activeTab,
  onTabChange,
}) => {
  const { activitiesData } = data;
  const { activities: activitiesActions } = actionMenuOptions;
  const [selectedActivity4Orgs, setSelectedActivity4Orgs] = useState();
  const { role } = useContext(SessionContext);

  const getTabState = (tabName) =>
    tabStates[tabName] || { loading: false, error: undefined, data: [] };

  const { loading: activitiesLoading, error: activitiesError } =
    getTabState("activities");

  const createTabHeader = (name, displayName, data, { loading, error }) => {
    const { length: dataLength = 0 } = data || {};

    return (
      <TabHeader name={name} className={`${name}-tab-header`}>
        <div className="tab-header-content">
          <div className="tab-header-text">{displayName}</div>
          <TabBadge count={dataLength} />
          {loading && <Loader className="btn-loader" />}
          {error && (
            <FontAwesomeIcon
              icon={["fa-solid", "exclamation-triangle"]}
              className="text-warning"
            />
          )}
        </div>
      </TabHeader>
    );
  };

  const tabConfigs = useMemo(() => {
    const configs = [
      {
        name: TAB_NAMES.ACTIVITIES,
        displayName: DATA_TYPES.ACTIVITIES,
        data: activitiesData,
        state: { loading: activitiesLoading, error: activitiesError },
        actionMenuOptions: [activitiesActions.edit],
        isMenuActionVisible: (activity, actionMenuOptions) => actionMenuOptions,
        openActivityOrgsModal: setSelectedActivity4Orgs,
        onRetry: () => onRetryTab?.("activities"),
        children: selectedActivity4Orgs && (
          <OrgsModal
            open={!!selectedActivity4Orgs}
            entity={selectedActivity4Orgs}
            nameProperty="title"
            onClose={() => setSelectedActivity4Orgs()}
            breadcrumbLabel="All Activities"
          />
        ),
      },
    ];

    return configs;
  }, [
    activitiesData,
    activitiesLoading,
    activitiesError,
    activitiesActions,
    selectedActivity4Orgs,
    role,
    onRetryTab,
  ]);

  const tabs = useMemo(
    () =>
      tabConfigs.map((config) => ({
        name: config.name,
        header: createTabHeader(
          config.name,
          config.displayName,
          config.data,
          config.state
        ),
        content: (
          <LibraryTab
            name={config.name}
            data={config.data}
            dataType={config.displayName}
            responsiveStyles={responsiveStyles}
            actionMenuOptions={config.actionMenuOptions}
            isMenuActionVisible={config.isMenuActionVisible}
            openActivityOrgsModal={config.openActivityOrgsModal}
            loading={config.state.loading}
            error={config.state.error}
            onRetry={config.onRetry}
          >
            {config.children}
          </LibraryTab>
        ),
      })),
    [tabConfigs, responsiveStyles]
  );

  const validTabs = tabs.filter(
    (tab) => tab && tab.name && tab.header && tab.content
  );

  const selectedTabName = activeTab || validTabs[0]?.name;

  const handleTabChange = (tabName) => {
    if (onTabChange) {
      onTabChange(tabName);
    }
  };

  return (
    <Tabs className="activities-tabs" selectedTabName={selectedTabName}>
      <TabHeaders onSelectedTabChange={handleTabChange}>
        {validTabs.map(({ header, name }, i) =>
          cloneElement(header, { key: `tab-header-${name}-${i}` })
        )}
      </TabHeaders>
      {validTabs.map(({ content, name }, i) =>
        cloneElement(content, { key: `tab-content-${name}-${i}` })
      )}
    </Tabs>
  );
};
